import { CartLinesUpdateEvent } from '@shopify/events';

class EgalexSetBuilder extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';

    this.form = this.querySelector('[data-set-form]');
    this.summaryLines = this.querySelector('[data-summary-lines]');
    this.total = this.querySelector('[data-set-total]');
    this.submitButton = this.querySelector('[data-set-submit]');
    this.status = this.querySelector('[data-set-status]');

    if (!this.form || !this.summaryLines || !this.total || !this.submitButton || !this.status) return;

    this.form.addEventListener('change', () => this.update());
    this.form.addEventListener('submit', (event) => this.addSet(event));
    this.update();
  }

  get selectedKit() {
    return this.form.querySelector('input[name="egalex-kit"]:checked');
  }

  get selectedMats() {
    return [...this.form.querySelectorAll('input[data-mat-variant]:checked')].filter((input) => input.value);
  }

  formatMoney(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'tr-TR', {
      style: 'currency',
      currency: this.dataset.currency || 'TRY',
      minimumFractionDigits: 2,
    }).format(cents / 100);
  }

  createSummaryLine(title, option, price) {
    const line = document.createElement('li');
    const label = document.createElement('span');
    const value = document.createElement('strong');

    label.textContent = `${title} · ${option} ${this.dataset.quantityLabel}`;
    value.textContent = this.formatMoney(price);
    line.append(label, value);
    return line;
  }

  update() {
    const kit = this.selectedKit;
    const mats = this.selectedMats;
    const fragment = document.createDocumentFragment();
    let total = 0;

    if (kit) {
      const price = Number(kit.dataset.price || 0);
      total += price;
      fragment.append(this.createSummaryLine(kit.dataset.title, kit.dataset.option, price));
    }

    for (const mat of mats) {
      const price = Number(mat.dataset.price || 0);
      total += price;
      fragment.append(this.createSummaryLine(mat.dataset.title, mat.dataset.option, price));
    }

    this.summaryLines.replaceChildren(fragment);
    this.total.textContent = this.formatMoney(total);
    this.submitButton.disabled = !kit || mats.length === 0;
    this.status.textContent = mats.length === 0 ? this.dataset.chooseMatMessage : '';
  }

  setLoading(isLoading) {
    this.submitButton.disabled = isLoading || !this.selectedKit || this.selectedMats.length === 0;
    this.submitButton.classList.toggle('is-loading', isLoading);
    this.submitButton.querySelector('[data-submit-label]').textContent = isLoading
      ? this.dataset.addingLabel
      : this.dataset.addLabel;
  }

  async addSet(event) {
    event.preventDefault();

    const kit = this.selectedKit;
    const mats = this.selectedMats;

    if (!kit || mats.length === 0) {
      this.update();
      this.status.focus();
      return;
    }

    const setId = `egalex-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const selected = [kit, ...mats];
    const items = selected.map((input) => ({
      id: Number(input.value),
      quantity: 1,
      properties: {
        _egalex_set_id: setId,
        _egalex_set_name: this.dataset.setName,
      },
    }));
    const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map((component) => component.dataset.sectionId)
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({
          merchandiseId: item.id.toString(),
          quantity: item.quantity,
        })),
        promise: deferred.promise,
      })
    );

    this.setLoading(true);
    this.status.textContent = '';

    try {
      const response = await fetch(this.dataset.cartAddUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          items,
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const result = await response.json();

      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || this.dataset.errorLabel);
      }

      const cartResponse = await fetch(`${this.dataset.cartUrl}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      const cart = await cartResponse.json();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'egalex-set-builder',
          sourceId: this.id,
          itemCount: items.length,
          sections: result.sections,
          didError: false,
        },
      });

      this.status.textContent = this.dataset.addedLabel;
      this.submitButton.querySelector('[data-submit-label]').textContent = this.dataset.addedLabel;
      setTimeout(() => {
        this.submitButton.querySelector('[data-submit-label]').textContent = this.dataset.addLabel;
      }, 1800);
    } catch (error) {
      deferred.reject(error);
      this.status.textContent = error instanceof Error ? error.message : this.dataset.errorLabel;
    } finally {
      this.setLoading(false);
    }
  }
}

if (!customElements.get('egalex-set-builder')) {
  customElements.define('egalex-set-builder', EgalexSetBuilder);
}
